import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { invokeFunctionWithAuth } from '@/lib/supabase/functions';
import type { Filmmaker } from '@/types';

interface FilmmakerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filmmaker?: Filmmaker | null;
  locationId: string;
  onSuccess: () => void;
}

export function FilmmakerDialog({ 
  open, 
  onOpenChange, 
  filmmaker,
  locationId,
  onSuccess 
}: FilmmakerDialogProps) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    bio: '',
    is_active: true,
  });

  // Normalize phone to E.164 format (only digits and + at the beginning)
  const normalizePhoneToE164 = (phone: string): string => {
    // Remove all non-digit characters except the leading +
    const cleaned = phone.trim();
    // If starts with +, keep it and remove all non-digits after
    if (cleaned.startsWith('+')) {
      return '+' + cleaned.slice(1).replace(/\D/g, '');
    }
    // Otherwise, remove all non-digits
    return cleaned.replace(/\D/g, '');
  };

  // Format phone as user types (display format)
  const formatPhoneDisplay = (value: string): string => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    
    // Don't format if empty or too long
    if (!digits || digits.length > 15) return value;
    
    // US/Canada format (+1 or just 1): (XXX) XXX-XXXX
    if (digits.length <= 10) {
      // Format without country code
      if (digits.length === 0) return '';
      if (digits.length <= 3) return `(${digits}`;
      if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    } else if (digits.startsWith('1') && digits.length === 11) {
      // US/Canada with country code 1
      const rest = digits.slice(1);
      return `+1 (${rest.slice(0, 3)}) ${rest.slice(3, 6)}-${rest.slice(6)}`;
    }
    
    // Default: just keep digits
    return digits;
  };

  useEffect(() => {
    if (filmmaker) {
      // Format existing phone for display
      const displayPhone = formatPhoneDisplay(filmmaker.phone);
      setFormData({
        full_name: filmmaker.full_name,
        email: filmmaker.email,
        phone: displayPhone,
        bio: filmmaker.bio,
        is_active: filmmaker.is_active,
      });
    } else {
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        bio: '',
        is_active: true,
      });
    }
  }, [filmmaker, open]);

  const validateForm = () => {
    if (!formData.full_name.trim()) {
      toast.error('Full name is required');
      return false;
    }
    if (!formData.email.trim() || !formData.email.includes('@')) {
      toast.error('Valid email is required');
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error('Phone number is required');
      return false;
    }
    if (!formData.bio.trim()) {
      toast.error('Bio is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    if (!locationId) {
      toast.error('Location not loaded yet. Please wait and try again.');
      return;
    }

    setLoading(true);

    try {
      if (filmmaker) {
        // Update existing filmmaker
        const { error: filmakerError } = await supabase
          .from('filmmakers')
          .update({
            bio: formData.bio.trim(),
            is_active: formData.is_active,
          })
          .eq('id', filmmaker.id);

        if (filmakerError) throw filmakerError;

        // Normalize phone before saving
        const normalizedPhone = normalizePhoneToE164(formData.phone);
        
        // Update profile
        const { error: profileError } = await supabase
          .from('profiles')
          .update({
            full_name: formData.full_name.trim(),
            email: formData.email.trim(),
            phone: normalizedPhone,
          })
          .eq('id', filmmaker.id);

        if (profileError) throw profileError;

        toast.success('Filmmaker updated successfully');
      } else {
        // Normalize phone before saving
        const normalizedPhone = normalizePhoneToE164(formData.phone);
        
        // Create new filmmaker using Edge Function
        const { data, error } = await invokeFunctionWithAuth('create-filmmaker', {
          full_name: formData.full_name.trim(),
          email: formData.email.trim(),
          phone: normalizedPhone,
          bio: formData.bio.trim(),
          location_id: locationId,
          is_active: formData.is_active,
        });

        if (error) {
          throw new Error(error.message || 'Failed to create filmmaker');
        }

        if (!data?.success) {
          throw new Error(data?.error || 'Failed to create filmmaker');
        }

        toast.success('Filmmaker created successfully');
      }

      onSuccess();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error saving filmmaker:', error);
      toast.error(error.message || 'Failed to save filmmaker');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {filmmaker ? 'Edit Filmmaker' : 'Add New Filmmaker'}
          </DialogTitle>
          <DialogDescription>
            {filmmaker 
              ? 'Update filmmaker information and settings'
              : 'Create a new filmmaker account and profile'
            }
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name *</Label>
              <Input
                id="full_name"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                placeholder="John Doe"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@example.com"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    // Format phone as user types
                    const formatted = formatPhoneDisplay(e.target.value);
                    setFormData({ ...formData, phone: formatted });
                  }}
                  onBlur={(e) => {
                    // Normalize phone when user leaves the field
                    const normalized = normalizePhoneToE164(e.target.value);
                    if (normalized !== e.target.value) {
                      setFormData({ ...formData, phone: normalized });
                    }
                  }}
                  placeholder="+1 (519) 890-6374"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  Format: +1 (519) 890-6374 (will be auto-formatted)
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio *</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Professional filmmaker with..."
                rows={3}
                required
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="is_active">Active Status</Label>
                <p className="text-sm text-muted-foreground">
                  Inactive filmmakers won't receive new bookings
                </p>
              </div>
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {filmmaker ? 'Update Filmmaker' : 'Create Filmmaker'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
